import { loginSuccess, logoutSuccess, nologin, themeChanged } from "../../store/spa-app-actions.js";
import { createLogger as createLogger$1 } from "../../helper/logger.js";
import { setRouteElement } from "../../store/spa-app-effects.js";
import { pdStore } from "../../store/mini-rx.store.js";
import { services } from "../../service-provider/ServiceRegistry.js";
import { getLoadingSelector } from "../../store/spa-app-selector.js";
import { POST_LOGIN_REDIRECT_KEY, pdRouterService } from "../../router/PdRouterService.js";
import { spaConfig } from "./spa-config.js";
import { SPA_EVENTS, dispatchInit } from "./spa-events.js";
import { ScrollController } from "./controllers/ScrollController.js";
import { fireOnceService } from "../../services/fire-once-service.js";
import { pwaInstallService } from "../../pwa/PwaInstallService.js";
import { pwaUpdateService } from "../../pwa/PwaUpdateService.js";
import { PD_THEME_CHANGED_EVENT } from "../../theme/theme-model.js";
import __decorate from "../../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-update-prompt/PdUpdatePrompt.js";
import "../default-login/DefaultLogin.js";
import "../default-not-found/DefaultNotFound.js";
import { LitElement, css, html } from "lit";
import { property, state } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";
import "@progressive-development/pd-page/pd-site-header";
import "@progressive-development/pd-page/pd-footer";
import { mapFooterBg, mapMenuBg, toastBus } from "@progressive-development/pd-page";
import { setCurrentFormatLocale } from "@progressive-development/pd-utils";
import "@progressive-development/pd-page/pd-toast";
import "@progressive-development/pd-content/pd-loading-state";
import "@progressive-development/pd-dialog/pd-popup";
//#region src/components/pd-spa-helper/PdSpaHelper.ts
/**
* PdSpaHelper - Abstract base class for SPA applications
*
* Provides core SPA functionality including:
* - Routing and navigation
* - Authentication state management
* - Loading state display
* - Toast notifications
* - Configurable layout (header, main, footer)
*/
var MADE_BY = {
	txt: "made by PD Progressive Development",
	email: "info@progressive-development.com"
};
var spaLogger = createLogger$1("PdSpaHelper");
/**
* Initialize the SPA framework.
*
* This function must be called before using PdSpaHelper components.
* It sets up:
* - Service provider (Firebase or Mock)
* - Redux store
* - Router with configured routes
*
* @param config - The application configuration
*
* @example
* ```typescript
* startInit({
*   serviceProvider: "firebase",
*   firebaseConfig: { ... },
*   navigationConfigParam: {
*     pages: [...],
*     includeLogin: true,
*   },
*   storeConfigParam: {
*     appName: "My App",
*     reducer: [...],
*     effects: [...],
*   },
* });
* ```
*/
var startInit = (config) => {
	spaLogger.debug("Starting SPA initialization", config);
	spaConfig.initialize(config);
	pwaInstallService.activate();
	pwaUpdateService.activate(config.pwaUpdateConfigParam);
	dispatchInit();
	spaLogger.debug("SPA initialization complete");
};
var THRESHOLD_DEFAULT = 100;
/**
* Abstract base class for SPA applications.
*
* Extend this class to create your main application component.
* Override the abstract methods to customize the layout and content.
*
* Required abstract methods:
* - `_renderRoutePages(route)` - Render content for given route
* - `_getFooter()` - Return footer configuration
* - `_getAppLogo()` - Return app logo template
* - `_setLocale()` - Handle locale changes
* - `_getAppConfiguration()` - Return app-specific config
*/
var PdSpaHelper = class extends LitElement {
	static {
		this.styles = css`
    /* ═══════════════════════════════════════════════════════════════════════
       CSS CUSTOM PROPERTIES
       ═══════════════════════════════════════════════════════════════════════ */
    :host {
      /* Single header height variable — set by pd-site-header height-change event */
      --pd-site-header-height: 90px;

      /* Layout */
      display: flex;
      flex-flow: column;
      height: 100%;
    }

    /* ═══════════════════════════════════════════════════════════════════════
       HEADER
       ═══════════════════════════════════════════════════════════════════════ */
    header {
      flex-grow: 0;
      position: fixed;
      top: 0;
      width: 100%;
      z-index: 99;
    }

    /* ═══════════════════════════════════════════════════════════════════════
       FOOTER
       ═══════════════════════════════════════════════════════════════════════ */
    footer {
      flex-grow: 0;
    }

    /* ═══════════════════════════════════════════════════════════════════════
       MAIN CONTENT
       ═══════════════════════════════════════════════════════════════════════ */
    main {
      flex-grow: 1;
      flex-basis: max-content;
    }

    header + main {
      padding-top: var(--pd-site-header-height, 90px);
    }

    /* ═══════════════════════════════════════════════════════════════════════
       LOGIN MODAL
       ═══════════════════════════════════════════════════════════════════════ */
    /* Öffnen läuft seit pd-dialog ≥1.4 über das startOpen-Flag am
     * Element — das frühere --pd-popup-default-display existiert im
     * <dialog>-basierten pd-popup nicht mehr. */
    .login-modal {
      --pd-popup-content-bg: var(--pd-default-bg-light-col);
    }
  `;
	}
	constructor() {
		super();
		this.scrollController = new ScrollController(this, {
			threshold: THRESHOLD_DEFAULT,
			onScrolledDown: () => {
				this.teaserCollapsed = true;
			},
			onScrolledUp: () => {
				this.teaserCollapsed = false;
			}
		});
		this.title = "PD SPA";
		this.teaserCollapseThreshold = THRESHOLD_DEFAULT;
		this._loadingState = [];
		this._route = "";
		this._path = "";
		this._params = {};
		this._query = {};
		this.teaserCollapsed = false;
		this._skipTeaserTransition = false;
		this._pendingScrollSectionId = null;
		this._claims = {};
		this._singleLocaleMenu = false;
		this._previousAuthState = "unknown";
		this._sectionRegistry = /* @__PURE__ */ new Map();
		this._sectionActions = /* @__PURE__ */ new Map();
		this._handleThemeChanged = (e) => {
			pdStore().dispatch(themeChanged(e.detail));
		};
		setRouteElement(this);
		document.addEventListener(SPA_EVENTS.INIT, () => {
			spaLogger.debug("SPA init event received");
		});
		if (spaConfig.includesLogin) {
			spaLogger.debug("Login included, activating login handler");
			this._activateLoginHandler();
		}
		this.addEventListener("route-event", this._handleRouteEvent);
		this.addEventListener("pd-action", this._handlePdAction);
	}
	connectedCallback() {
		super.connectedCallback();
		pdRouterService.onRouteChange(({ route, path, params, query }) => {
			const previousRoute = this._route;
			const isRouteChange = route !== previousRoute;
			const isLoginModal = route === "login";
			if (isRouteChange && !isLoginModal) this.scrollController.scrollToTop();
			if (isRouteChange && !isLoginModal && previousRoute) this._sectionRegistry.delete(previousRoute);
			this._route = route;
			this._path = path;
			this._params = params;
			this._query = query;
		});
		this.addEventListener(SPA_EVENTS.REGISTER_SECTIONS, this._handleRegisterSections);
		this._subscription = pdStore().select(getLoadingSelector).subscribe((loadingState) => {
			this._loadingState = loadingState;
		});
		this.addEventListener(SPA_EVENTS.APP_CONFIG, this._handleAppConfigEvent);
		this.addEventListener("section-activated", this._handleSectionActivated);
		document.addEventListener(PD_THEME_CHANGED_EVENT, this._handleThemeChanged);
		this.scrollController.setThreshold(this.teaserCollapseThreshold);
	}
	disconnectedCallback() {
		this._subscription?.unsubscribe();
		document.removeEventListener(PD_THEME_CHANGED_EVENT, this._handleThemeChanged);
		this.removeEventListener(SPA_EVENTS.APP_CONFIG, this._handleAppConfigEvent);
		this.removeEventListener(SPA_EVENTS.REGISTER_SECTIONS, this._handleRegisterSections);
		this.removeEventListener("section-activated", this._handleSectionActivated);
		super.disconnectedCallback();
	}
	willUpdate(changedProps) {
		super.willUpdate(changedProps);
		if (changedProps.has("_route") && this._route) {
			const hasTeaser = !!spaConfig.getNavigationPage(this._route)?.withTeaser && this._hasTeaserContent();
			this.scrollController.setTeaserCollapseEnabled(hasTeaser);
			if (this._pendingScrollSectionId) {
				this.teaserCollapsed = true;
				this._skipTeaserTransition = true;
			} else {
				this._skipTeaserTransition = false;
				this.teaserCollapsed = !hasTeaser;
			}
		}
	}
	render() {
		if (this._route === "") return this._renderLoading();
		const configRoute = this._route === "login" ? pdRouterService.previousRoute || "home" : this._route;
		const pageConf = spaConfig.getNavigationPage(configRoute);
		const showTeaser = !!pageConf?.withTeaser && this._hasTeaserContent();
		return this._renderLayout(pageConf, showTeaser);
	}
	/**
	* Render the loading state while route is being resolved
	* Can be overridden by subclass
	*/
	_renderLoading() {
		return html`<slot name="loading"><p>Loading...</p></slot>`;
	}
	/**
	* Render the main layout
	* Can be overridden by subclass for custom layouts
	*/
	_renderLayout(pageConf, showTeaser) {
		const isLoginModal = this._route === "login";
		const backgroundRoute = pdRouterService.previousRoute || "home";
		const contentRoute = isLoginModal ? backgroundRoute : this._route;
		return html`
      ${pageConf?.hideMenu ? "" : html`<header>
              ${this._renderSiteHeader(pageConf, showTeaser)}
            </header>`}

      <main class="${this._getMainClass(pageConf)}">
        ${this._loadingState.map((ls) => html`<pd-loading-state
              .loadingState="${ls}"
              .loaderStyle="${ls.loaderStyle || spaConfig.defaultLoaderStyle || "spinner"}"
            ></pd-loading-state>`)}
        ${this._renderRoutePages(contentRoute)}
        ${this._route === "not-found" ? html`<default-not-found></default-not-found>` : ""}
      </main>

      ${isLoginModal ? this._renderLoginModal() : ""}
      ${pageConf?.withFooter ? html`<footer>${this._renderFooter()}</footer>` : ""}

      <pd-toast></pd-toast>
      ${this._renderUpdatePrompt()} ${this._renderOverlays()}
    `;
	}
	/**
	* Render the built-in service-worker update dialog (pd-update-prompt).
	* Texts and behaviour come from `startInit({ pwaUpdateConfigParam })`;
	* `enabled: false` hides it. Override for a fully custom update UI (own
	* i18n, own dialog) — the pwaUpdateService keeps running either way. In
	* apps without a service worker the prompt never shows.
	*/
	_renderUpdatePrompt() {
		const conf = spaConfig.pwaUpdateConfig;
		if (conf?.enabled === false) return html``;
		return html`<pd-update-prompt .texts="${conf?.texts}"></pd-update-prompt>`;
	}
	/**
	* Hook for app-global overlays rendered on every route, outside the
	* page content — e.g. a consent banner or global dialog. Rendered at the
	* end of the layout (after the toast). Default: empty.
	*
	* @example
	* ```typescript
	* protected override _renderOverlays(): TemplateResult {
	*   return html`<app-consent-banner></app-consent-banner>`;
	* }
	* ```
	*/
	_renderOverlays() {
		return html``;
	}
	/**
	* Render the login modal popup.
	*
	* Contract: mounten = offen — das Popup wird routen-gesteuert
	* konditional gerendert und öffnet über startOpen sofort; geschlossen
	* wird über popup-close (ESC → Navigation zurück) bzw. Unmount beim
	* Routenwechsel.
	*/
	_renderLoginModal() {
		return html`
      <pd-popup
        class="login-modal"
        startOpen
        closeByEscape
        popupLabel="Anmeldung"
        @popup-close="${this._handleLoginPopupClose}"
      >
        <default-login slot="content"></default-login>
      </pd-popup>
    `;
	}
	/**
	* Handle login popup close (user aborted login via Escape or backdrop click)
	*/
	_handleLoginPopupClose() {
		const backRoute = pdRouterService.previousRoute || "/";
		pdRouterService.navigate(backRoute);
	}
	_renderSiteHeader(pageConf, showTeaser) {
		const { bgImage, styles: bgStyles } = mapMenuBg(pageConf?.bgConfig);
		return html`
      <pd-site-header
        id="pdHelperSiteHeaderId"
        style="${styleMap(bgStyles)}"
        bg-image="${bgImage}"
        .topMenuItems=${this._getVisibleTopMenuItems(pageConf)}
        .menuItems=${this._getVisibleMenuItems(pageConf)}
        selectedLocale="${this._selectedLocale}"
        .locales=${this._availableLocales}
        ?singleLocaleMenu=${this._singleLocaleMenu}
        activeRoute="${this._path}"
        logoRoute="${pageConf?.menuLogoRoute || ""}"
        .showTeaser=${showTeaser}
        .teaserCollapsed=${this.teaserCollapsed}
        .skipTeaserTransition=${this._skipTeaserTransition}
        topMenuMode="${pageConf?.topMenuMode || "detached"}"
        @locale-change="${this._handleLocaleChange}"
        @height-change="${this._handleHeaderHeightChange}"
      >
        ${this._getAppLogo()}
        ${showTeaser ? this._renderTeaserSlotContent() : ""}
      </pd-site-header>
    `;
	}
	_getVisibleMenuItems(pageConf) {
		return (pageConf?.menu || []).filter((item) => !item.requiredRoles?.length || item.requiredRoles.some((role) => this._claims?.[role] === true));
	}
	_getVisibleTopMenuItems(pageConf) {
		if (pageConf?.hideTopMenu) return [];
		return (pageConf?.topMenuItems || []).filter((item) => !item.requiredRoles?.length || item.requiredRoles.some((role) => this._claims?.[role] === true));
	}
	_renderFooter() {
		const footer = this._getFooter();
		const pdSections = footer.sections?.map((s) => ({
			heading: s.heading,
			links: s.links.map((l) => ({
				key: l.key,
				name: l.name
			}))
		})) ?? [];
		const pdLegalLinks = footer.legalLinks?.map((l) => ({
			key: l.key,
			name: l.name
		})) ?? [];
		const { bgImage, styles: bgStyles } = mapFooterBg(footer.bgConfig);
		return html`
      <pd-footer
        style="${styleMap(bgStyles)}"
        bg-image="${bgImage}"
        .sections="${pdSections}"
        .socialLinks="${footer.socialLinks ?? []}"
        .legalLinks="${pdLegalLinks}"
        copyright="${footer.copyright ?? "PD Progressive Development UG"}"
        version="${footer.version || ""}"
        ?show-version="${footer.showVersion ?? false}"
        .madeBy="${footer.madeBy ?? MADE_BY}"
        @footer-link="${this._handleFooterLink}"
      >
        ${this._renderFooterSlotContent()}
      </pd-footer>
    `;
	}
	/**
	* Render slot content for pd-footer (slots `logo` and `extra`), e.g. a
	* brand block with logo, company name and address. Default: empty.
	* Override in subclass — the returned template must use `slot="logo"`
	* and/or `slot="extra"` on its root elements. Same pattern as
	* `_renderTeaserSlotContent()`.
	*
	* @example
	* ```typescript
	* protected override _renderFooterSlotContent(): TemplateResult {
	*   return html`<div slot="logo"><img src=${logo} /> ACME GmbH</div>`;
	* }
	* ```
	*/
	_renderFooterSlotContent() {
		return html``;
	}
	/**
	* Whether the app has teaser content. Default: false.
	* Override in subclass to enable teaser rendering.
	* Used together with pageConf.withTeaser to determine if the teaser is shown.
	*/
	_hasTeaserContent() {
		return false;
	}
	/**
	* Render teaser slot content for pd-site-header.
	* Override in subclass (or intermediate layer like PdVisitcardApp)
	* to provide teaser content. The returned template must include
	* `slot="teaser-content"` on its root element.
	*
	* @example
	* ```typescript
	* protected override _renderTeaserSlotContent(): TemplateResult {
	*   return html`<img slot="teaser-content" src="hero.jpg" />`;
	* }
	* ```
	*/
	_renderTeaserSlotContent() {
		return html``;
	}
	_handleRouteEvent(e) {
		if (e.detail.el && !e.detail.route) this._scrollToSection(e.detail.el);
		else if (e.detail.route) {
			if (e.detail.sectionId) this._pendingScrollSectionId = e.detail.sectionId;
			this._navigateToPage(e.detail.route);
		}
	}
	_handleFooterLink(e) {
		const { key } = e.detail.linkObj;
		const footer = this._getFooter();
		const linkObj = [...footer.sections?.flatMap((s) => s.links) ?? [], ...footer.legalLinks ?? []].find((l) => l.key === key);
		if (linkObj?.link) this._navigateToPage(linkObj.link);
		if (linkObj?.action) linkObj.action();
	}
	/**
	* Handle pd-action events dispatched by scoped interceptors on data-pd-action elements.
	* Handles generic actions (route, section). Override in subclass for app-specific
	* actions (e.g., "wizard").
	*
	* Unknown actions are silently ignored.
	* "url" actions are handled natively by the browser via href — no handler needed.
	*/
	_handlePdAction(e) {
		const { action, target } = e.detail;
		switch (action) {
			case "route":
				this._navigateToPage(target);
				break;
			case "section": this._navigateToSection(target);
		}
	}
	/**
	* Scroll to a section by its ID on the current page. O(1) registry lookup;
	* works for any registered section regardless of menu membership.
	*
	* Cross-page navigate-and-scroll uses `route-event` with `{ route, sectionId }`
	* (same mechanism pd-menu uses) — `_pendingScrollSectionId` is applied once
	* the target page dispatches pd-spa:register-sections.
	*/
	_navigateToSection(sectionId) {
		const element = this._sectionRegistry.get(this._route)?.get(sectionId);
		if (element) this._scrollToSection(element);
	}
	_handleRegisterSections(e) {
		const { pageName, sections, sectionActions } = e.detail;
		const pageMap = /* @__PURE__ */ new Map();
		for (const [id, el] of Object.entries(sections)) if (el) pageMap.set(id, el);
		this._sectionRegistry.set(pageName, pageMap);
		const pageConf = spaConfig.getNavigationPage(pageName);
		const hydrateRefs = (items) => items.map((menuElement) => ({
			...menuElement,
			ref: menuElement.sec ? pageMap.get(menuElement.sec) : void 0
		}));
		let menusTouched = false;
		if (pageConf?.menu) {
			pageConf.menu = hydrateRefs(pageConf.menu);
			menusTouched = true;
		}
		if (pageConf?.topMenuItems) {
			pageConf.topMenuItems = hydrateRefs(pageConf.topMenuItems);
			menusTouched = true;
		}
		if (menusTouched) this.requestUpdate();
		if (sectionActions) for (const [sectionId, actionCreator] of Object.entries(sectionActions)) this._sectionActions.set(sectionId, actionCreator);
		if (this._pendingScrollSectionId && pageName === this._route) {
			const element = pageMap.get(this._pendingScrollSectionId);
			if (element) requestAnimationFrame(() => {
				this._scrollToSection(element);
				this._skipTeaserTransition = false;
			});
			else this._skipTeaserTransition = false;
			this._pendingScrollSectionId = null;
		}
	}
	_handleSectionActivated(e) {
		const { sectionId } = e.detail;
		const actionCreator = this._sectionActions.get(sectionId);
		if (actionCreator) fireOnceService.fireOnce(sectionId, actionCreator());
	}
	_handleLocaleChange(e) {
		spaLogger.debug("Handle change locale", e);
		const newLocale = e.detail;
		this._setLocale(newLocale);
		this._selectedLocale = newLocale;
		setCurrentFormatLocale(newLocale);
	}
	_handleAppConfigEvent(event) {
		const customEvent = event;
		if (customEvent.detail) customEvent.detail.appConf = this._getAppConfiguration();
	}
	_activateLoginHandler() {
		spaLogger.debug("Activating login handler");
		services.auth.onAuthStateChanged(async (user) => {
			spaLogger.debug("Auth state changed", user?.email);
			const newState = user ? "logged-in" : "logged-out";
			if (user) {
				const claims = (await services.auth.getIdTokenResult())?.claims || {};
				this._claims = claims;
				pdStore().dispatch(loginSuccess({
					uid: user.uid,
					email: user.email,
					emailVerified: user.emailVerified,
					claims,
					providerId: "unknown",
					isAnonymous: user.isAnonymous ?? false
				}));
				const redirectRoute = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
				if (redirectRoute) {
					const routeToNavigate = redirectRoute && redirectRoute !== "/login" ? redirectRoute : "";
					sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
					spaLogger.debug("Redirecting after login to:", routeToNavigate);
					this._navigateToPage(routeToNavigate);
				}
				if (this._previousAuthState === "logged-out") toastBus.success(`Angemeldet als ${user.email}`);
			} else {
				pdStore().dispatch(nologin());
				this._claims = {};
				if (this._previousAuthState === "logged-in") {
					pdStore().dispatch(logoutSuccess());
					toastBus.info("Abgemeldet");
					this._navigateToPage(spaConfig.logoutRedirect);
				}
			}
			this._previousAuthState = newState;
		});
	}
	_getMainClass(pageConf) {
		return pageConf?.mainClass || "";
	}
	_handleHeaderHeightChange(e) {
		this.style.setProperty("--pd-site-header-height", `${e.detail.height}px`);
	}
	_navigateToPage(routeURL) {
		pdRouterService.navigate(routeURL);
	}
	/**
	* Scroll to a section element. Collapses teaser if expanded.
	*/
	_scrollToSection(element) {
		if (!this.teaserCollapsed) this.teaserCollapsed = true;
		element.scrollIntoView({
			behavior: "smooth",
			block: "start"
		});
	}
};
__decorate([property({ type: String })], PdSpaHelper.prototype, "title", void 0);
__decorate([property({
	type: Number,
	attribute: "teaser-collapse-threshold"
})], PdSpaHelper.prototype, "teaserCollapseThreshold", void 0);
__decorate([state()], PdSpaHelper.prototype, "_loadingState", void 0);
__decorate([state()], PdSpaHelper.prototype, "_route", void 0);
__decorate([state()], PdSpaHelper.prototype, "_path", void 0);
__decorate([state()], PdSpaHelper.prototype, "_params", void 0);
__decorate([state()], PdSpaHelper.prototype, "_query", void 0);
__decorate([property({
	type: Boolean,
	reflect: true,
	attribute: "teaser-collapsed"
})], PdSpaHelper.prototype, "teaserCollapsed", void 0);
__decorate([property({
	type: Boolean,
	reflect: true,
	attribute: "skip-teaser-transition"
})], PdSpaHelper.prototype, "_skipTeaserTransition", void 0);
__decorate([state()], PdSpaHelper.prototype, "_claims", void 0);
//#endregion
export { PdSpaHelper, startInit };
